/**
 * Configuration lue au demarrage, jamais compilee dans le paquet.
 *
 * Vite remplace `import.meta.env.*` a la compilation. Une URL d'API ecrite dans
 * le code serait donc scellee dans l'image Docker, et il faudrait une image par
 * environnement — exactement ce que « aucune valeur codee en dur » interdit.
 *
 * L'ordre de resolution est le suivant :
 *
 * 1. `window.__MIAMIAM_BACKOFFICE__`, ecrit par `runtime-config.js` que
 *    l'entrypoint du conteneur genere depuis ses variables d'environnement.
 *    C'est la source de verite en production.
 * 2. `import.meta.env.VITE_API_URL`, pour le serveur de developpement Vite.
 * 3. Rien : l'application refuse de demarrer avec un message explicite plutot
 *    que d'appeler une URL devinee.
 *
 * Il n'y a **aucun repli sur `http://localhost:8080`**. Un tel defaut donnerait
 * une application qui semble fonctionner sur le poste de son auteur et echoue
 * partout ailleurs, avec une erreur reseau illisible en guise d'explication.
 */

export interface RuntimeConfig {
  /** URL de base de l'API, sans barre oblique finale. Exemple : `http://localhost:8080/api/v1`. */
  readonly apiUrl: string;
}

/** Nom de la variable globale ecrite par `runtime-config.js`. */
const RUNTIME_GLOBAL = '__MIAMIAM_BACKOFFICE__';

/**
 * Valeur laissee par l'entrypoint quand la variable d'environnement est absente.
 * Elle est traitee comme une absence, pas comme une URL : sans elle, un
 * `docker run` sans configuration produirait des appels vers l'URL litterale
 * `__MIAMIAM_API_URL__`.
 */
const UNSUBSTITUTED_PLACEHOLDER = '__MIAMIAM_API_URL__';

export class MissingRuntimeConfigError extends Error {
  constructor() {
    super(
      "L'URL de l'API n'est pas configurée. " +
        'En conteneur, renseignez la variable d’environnement MIAMIAM_API_URL. ' +
        'En développement, renseignez VITE_API_URL dans backoffice/.env.local.',
    );
    this.name = 'MissingRuntimeConfigError';
  }
}

/**
 * Lecture du global injecte au demarrage.
 *
 * Sortie en `unknown` puis validee : `window` est une frontiere de donnees comme
 * une autre, et le fichier injecte est ecrit par un script shell.
 */
const readInjectedApiUrl = (source: typeof globalThis): string | null => {
  const injected: unknown = (source as unknown as Record<string, unknown>)[RUNTIME_GLOBAL];

  if (typeof injected !== 'object' || injected === null) {
    return null;
  }

  const apiUrl: unknown = (injected as Record<string, unknown>).apiUrl;

  if (typeof apiUrl !== 'string') {
    return null;
  }

  const trimmed = apiUrl.trim();

  if (trimmed === '' || trimmed === UNSUBSTITUTED_PLACEHOLDER) {
    return null;
  }

  return trimmed;
};

const readBuildTimeApiUrl = (env: Record<string, unknown> | undefined): string | null => {
  const apiUrl = env?.VITE_API_URL;

  if (typeof apiUrl !== 'string') {
    return null;
  }

  const trimmed = apiUrl.trim();

  return trimmed === '' ? null : trimmed;
};

/** Retire la barre oblique finale pour que la concatenation des chemins reste previsible. */
const normalizeBaseUrl = (apiUrl: string): string => apiUrl.replace(/\/+$/, '');

/**
 * @param source globals a inspecter — parametre explicite pour que les tests
 *               n'aient pas a muter `window`
 * @param env    variables figees a la compilation
 * @throws MissingRuntimeConfigError si aucune URL n'est configuree
 */
export const resolveRuntimeConfig = (
  source: typeof globalThis = globalThis,
  env: Record<string, unknown> | undefined = import.meta.env,
): RuntimeConfig => {
  const apiUrl = readInjectedApiUrl(source) ?? readBuildTimeApiUrl(env);

  if (apiUrl === null) {
    throw new MissingRuntimeConfigError();
  }

  return { apiUrl: normalizeBaseUrl(apiUrl) };
};

/**
 * Environnement de deploiement, pour le bandeau de la coque (ADR 0035 §2).
 *
 * Meme mecanisme que l'URL : `runtime-config.js` porte le marqueur
 * `__MIAMIAM_ENVIRONMENT__`, que `docker/entrypoint.sh` remplace depuis
 * `MIAMIAM_ENVIRONMENT`. **Aucun repli** : une variable absente, le marqueur
 * laisse en place ou une valeur hors enumeration rendent `null`, que la coque
 * affiche « Environnement non renseigné ». Supposer « prod » par defaut
 * habituerait a voir le bandeau rouge partout et le priverait de son sens la ou
 * il compte ; supposer « dev » ferait pire.
 *
 * Lecture separee de `resolveRuntimeConfig` : l'URL est obligatoire (sans elle
 * l'application ne demarre pas), l'environnement ne l'est pas.
 */
export const DEPLOYMENT_ENVIRONMENTS = ['prod', 'recette', 'dev'] as const;

export type DeploymentEnvironment = (typeof DEPLOYMENT_ENVIRONMENTS)[number];

const isDeploymentEnvironment = (value: string): value is DeploymentEnvironment =>
  (DEPLOYMENT_ENVIRONMENTS as readonly string[]).includes(value);

export const resolveEnvironment = (source: typeof globalThis = globalThis): DeploymentEnvironment | null => {
  const injected: unknown = (source as unknown as Record<string, unknown>)[RUNTIME_GLOBAL];

  if (typeof injected !== 'object' || injected === null) {
    return null;
  }

  const environment: unknown = (injected as Record<string, unknown>).environment;

  // Ni casse ni espaces corriges : « PROD » ou « production » sont des fautes de
  // configuration, et les rendre lisibles les ferait durer.
  return typeof environment === 'string' && isDeploymentEnvironment(environment) ? environment : null;
};
